/**
 * Does this tenant hold a section? One rule, one home.
 *
 * WHY THIS FILE EXISTS (2026-09-30). The same question was answered in three
 * places, and one of them answered it wrongly:
 *
 *   apps/api/src/lib/tenantSections.ts  onShowList        — correct
 *   apps/web/src/layout/Sidebar.tsx     tenantHasSection  — correct, tested
 *   apps/web/src/lib/useSectionTiers.ts useHasSection     — WRONG
 *
 * `useHasSection` did a bare `selected.includes(key)`. That is right for a
 * tenant somebody has configured and wrong for everybody else, because AN
 * EMPTY `selectedSections` MEANS "NOT SET UP, SHOW EVERYTHING" — not "holds
 * nothing". The schema says so, the server's onShowList says so, and the
 * sidebar has always behaved that way. So on an unconfigured tenant the
 * sidebar drew a section while a button inside that same page decided the
 * tenant did not have it. Its own docstring promised the opposite: *"Same
 * question the sidebar asks, so the nav entry and the buttons inside the page
 * cannot disagree."*
 *
 * It is not hypothetical: `gb-test-tenant`, the tenant this platform is
 * developed against, has `selected_sections = {}` and holds every section.
 * Anything gated on the old helper was invisible on the one tenant most likely
 * to be looked at.
 */
import { OPT_IN_SECTION_KEYS } from '@jeanieiq/types';
import { useCurrentTenant } from './useCurrentTenant';

/**
 * The show/deny rule, identical to `onShowList` in the API.
 *
 *   selected has entries → it IS the list; anything not on it is not held.
 *   selected is empty    → nobody has configured this tenant; fall back to the
 *                          deny list and behave as before, except for an
 *                          opt-in section (OPT_IN_SECTION_KEYS), which only a
 *                          tick gives.
 */
export function tenantHoldsSection(
  key: string,
  selectedSections: readonly string[],
  hiddenSections: readonly string[],
): boolean {
  if (selectedSections.length > 0) return selectedSections.includes(key);
  if ((OPT_IN_SECTION_KEYS as readonly string[]).includes(key)) return false;
  return !hiddenSections.includes(key);
}

/**
 * The same question for the signed-in tenant.
 *
 * Returns FALSE while the profile is still loading. That is deliberate for the
 * thing this was written for — revealing a section's extras inside a shared
 * screen — because a control that appears and then vanishes is worse than one
 * that appears a moment late. A caller that needs to tell "no" from "not yet"
 * should read `useCurrentTenant()` itself.
 */
export function useTenantHoldsSection(key: string): boolean {
  const tenantQ = useCurrentTenant();
  const t = tenantQ.data;
  if (!t) return false;
  return tenantHoldsSection(key, t.selectedSections ?? [], t.hiddenSections ?? []);
}
